import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import DataTable from "react-data-table-component";
import { Badge, Container, Input } from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import ArtistService from "../../../Services/artists/ArtistService";
import SpinnerLoader from "../../Loaders/Spinner";
import ArtistDetailModal from "./ArtistDetailModal";
import "./artists-list.scss";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const tableStyles = {
  headCells: {
    style: {
      fontSize: "0.8125rem",
      fontWeight: 600,
      paddingLeft: "12px",
      paddingRight: "12px",
    },
  },
  cells: {
    style: {
      fontSize: "0.875rem",
      paddingLeft: "12px",
      paddingRight: "12px",
    },
  },
};

function ArtistsContainer() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [youtubeFilter, setYoutubeFilter] = useState("all");
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const [labelFilter, setLabelFilter] = useState("all");
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const { isPending, isError, data, error } = useQuery({
    queryKey: ["artists"],
    queryFn: ArtistService.getAllArtists,
  });

  const artists = data?.data ?? [];

  const currencies = useMemo(() => {
    const set = new Set(
      artists.map((artist) => artist.currency).filter(Boolean)
    );
    return Array.from(set).sort();
  }, [artists]);

  const labels = useMemo(() => {
    const map = new Map();
    artists.forEach((artist) => {
      if (artist.label_id && artist.label_name) {
        map.set(String(artist.label_id), artist.label_name);
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [artists]);

  const filteredArtists = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return artists.filter((artist) => {
      const matchesSearch =
        !term ||
        [
          artist.artist_name,
          artist.full_name,
          artist.artist_email,
          artist.red_artist_id,
          artist.label_name,
          artist.phone_number,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && artist.is_active) ||
        (statusFilter === "inactive" && !artist.is_active);

      const matchesYoutube =
        youtubeFilter === "all" ||
        (youtubeFilter === "connected" && artist.youtube_connected) ||
        (youtubeFilter === "not_connected" && !artist.youtube_connected);

      const matchesCurrency =
        currencyFilter === "all" || artist.currency === currencyFilter;

      const matchesLabel =
        labelFilter === "all" || String(artist.label_id) === labelFilter;

      return (
        matchesSearch &&
        matchesStatus &&
        matchesYoutube &&
        matchesCurrency &&
        matchesLabel
      );
    });
  }, [
    artists,
    searchTerm,
    statusFilter,
    youtubeFilter,
    currencyFilter,
    labelFilter,
  ]);

  const stats = useMemo(
    () => ({
      total: artists.length,
      active: artists.filter((artist) => artist.is_active).length,
      youtubeLinked: artists.filter((artist) => artist.youtube_connected)
        .length,
      labels: labels.length,
    }),
    [artists, labels.length]
  );

  const handleViewArtist = (artist) => {
    setSelectedArtist(artist);
    setIsDetailOpen(true);
  };

  const tableColumns = useMemo(
    () => [
      {
        name: "Artist",
        selector: (row) => row.artist_name,
        sortable: true,
        minWidth: "120px",
        grow: 1.5,
        cell: (row) => (
          <div className="art-name-cell">
            <div className="art-name-cell__name" title={row.artist_name}>
              {row.artist_name || "—"}
            </div>
            {row.full_name && row.full_name !== row.artist_name ? (
              <div className="art-name-cell__meta" title={row.full_name}>
                {row.full_name}
              </div>
            ) : row.red_artist_id ? (
              <div className="art-name-cell__meta">ID: {row.red_artist_id}</div>
            ) : null}
          </div>
        ),
      },
      {
        name: "Label",
        selector: (row) => row.label_name,
        sortable: true,
        minWidth: "110px",
        cell: (row) =>
          row.label_name ? (
            <div className="art-label-cell">
              <div className="art-label-cell__name" title={row.label_name}>
                {row.label_name}
              </div>
              {row.label_red_id ? (
                <div className="art-label-cell__meta">
                  CMS ID: {row.label_red_id}
                </div>
              ) : null}
            </div>
          ) : (
            "—"
          ),
      },
      {
        name: "Email",
        selector: (row) => row.artist_email,
        sortable: true,
        minWidth: "120px",
        grow: 1.2,
        cell: (row) =>
          row.artist_email ? (
            <span className="art-email-cell" title={row.artist_email}>
              {row.artist_email}
            </span>
          ) : (
            "—"
          ),
      },
      {
        name: "Currency",
        selector: (row) => row.currency,
        sortable: true,
        center: true,
        width: "100px",
        cell: (row) => row.currency || "—",
      },
      {
        name: "YouTube",
        selector: (row) => row.youtube_connected,
        sortable: true,
        center: true,
        width: "110px",
        cell: (row) =>
          row.youtube_connected ? (
            <Badge color="success" className="art-badge">
              Linked
            </Badge>
          ) : (
            <Badge color="light" className="art-badge text-dark">
              Unlinked
            </Badge>
          ),
      },
      {
        name: "Status",
        selector: (row) => row.is_active,
        sortable: true,
        center: true,
        width: "100px",
        cell: (row) =>
          row.is_active ? (
            <Badge color="success" className="art-badge">
              Active
            </Badge>
          ) : (
            <Badge color="secondary" className="art-badge">
              Inactive
            </Badge>
          ),
      },
      {
        name: "Updated",
        selector: (row) => row.updated_at,
        sortable: true,
        width: "120px",
        cell: (row) => formatDate(row.updated_at),
      },
      {
        name: "Actions",
        center: true,
        width: "88px",
        minWidth: "88px",
        cell: (row) => (
          <div className="art-actions">
            <button
              type="button"
              className="art-action-btn"
              title="View details"
              aria-label={`View details for ${row.artist_name || "artist"}`}
              onClick={() => handleViewArtist(row)}
            >
              <i className="fa fa-eye" aria-hidden="true" />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  if (isError) {
    return (
      <div className="alert alert-danger m-3">
        Failed to load artists: {error?.message || "Unknown error"}
      </div>
    );
  }

  return (
    <>
      <Breadcrumbs parent="Artists" title="Artists" mainTitle="Artists" />

      <div className="rd-artists-list">
        <Container fluid>
          <div className="art-stats">
            <div className="art-stat">
              <p className="art-stat__label">Total</p>
              <p className="art-stat__value">{stats.total}</p>
            </div>
            <div className="art-stat">
              <p className="art-stat__label">Active</p>
              <p className="art-stat__value">{stats.active}</p>
            </div>
            <div className="art-stat">
              <p className="art-stat__label">YouTube linked</p>
              <p className="art-stat__value">{stats.youtubeLinked}</p>
            </div>
            <div className="art-stat">
              <p className="art-stat__label">Labels</p>
              <p className="art-stat__value">{stats.labels}</p>
            </div>
          </div>

          <div className="art-toolbar">
            <div className="art-toolbar__search">
              <Input
                type="search"
                placeholder="Search name, email, Red ID, label…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Input
              type="select"
              className="art-toolbar__filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Input>
            <Input
              type="select"
              className="art-toolbar__filter"
              value={youtubeFilter}
              onChange={(e) => setYoutubeFilter(e.target.value)}
            >
              <option value="all">All YouTube</option>
              <option value="connected">Linked</option>
              <option value="not_connected">Unlinked</option>
            </Input>
            <Input
              type="select"
              className="art-toolbar__filter"
              value={currencyFilter}
              onChange={(e) => setCurrencyFilter(e.target.value)}
            >
              <option value="all">All currencies</option>
              {currencies.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </Input>
            <Input
              type="select"
              className="art-toolbar__filter"
              value={labelFilter}
              onChange={(e) => setLabelFilter(e.target.value)}
            >
              <option value="all">All labels</option>
              {labels.map((label) => (
                <option key={label.id} value={label.id}>
                  {label.name}
                </option>
              ))}
            </Input>
          </div>

          <div className="art-panel">
            {isPending ? (
              <SpinnerLoader />
            ) : filteredArtists.length === 0 ? (
              <div className="art-empty">No artists match your filters.</div>
            ) : (
              <div className="art-table-wrap">
                <DataTable
                  columns={tableColumns}
                  data={filteredArtists}
                  pagination
                  paginationPerPage={25}
                  paginationRowsPerPageOptions={[10, 25, 50, 100]}
                  highlightOnHover
                  responsive
                  customStyles={tableStyles}
                  noDataComponent="No artists found."
                />
              </div>
            )}
          </div>
        </Container>
      </div>

      <ArtistDetailModal
        isOpen={isDetailOpen}
        toggle={() => setIsDetailOpen(false)}
        artist={selectedArtist}
      />
    </>
  );
}

export default ArtistsContainer;
